import { motion } from "framer-motion";
import { Globe, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatGPTLogo, GeminiLogo, PerplexityLogo } from "./PlatformLogos";
import { useShouldReduceAnimations } from "@/hooks/useReducedMotion";

const Services = () => {
  const reduceAnimations = useShouldReduceAnimations();
  const services = [
    {
      Logo: ChatGPTLogo,
      name: "ChatGPT Ads",
      description: "The world's largest AI assistant. 100M+ weekly users searching for services like yours.",
      color: "from-emerald-500 to-teal-500",
      status: "Coming Soon",
    },
    {
      Logo: GeminiLogo,
      name: "Gemini Ads",
      description: "Google's AI powerhouse. Integrated across Android, Search, and Workspace.",
      color: "from-blue-500 to-cyan-500",
      status: "Coming Soon",
    },
    {
      Logo: PerplexityLogo,
      name: "Perplexity Ads",
      description: "The AI-native search engine. Direct answers with sponsored placements.",
      color: "from-violet-500 to-purple-500",
      status: "Coming Soon",
    },
  ];

  return (
    <section id="services" className="py-12 md:py-16 relative">
      <div className="container mx-auto px-4 max-w-[1280px]">
        <motion.div
          initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reduceAnimations ? 0 : 0.4 }}
          className="text-center space-y-4 mb-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            3 AI Platforms —{" "}
            <span className="text-gradient">One Dashboard</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            We're building the infrastructure to run your ads across all major AI platforms. 
            Get in early and dominate before your competitors catch on.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: reduceAnimations ? 0 : 0.3, delay: reduceAnimations ? 0 : index * 0.05 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-300" 
                   style={{ backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }} />
              
              <div className="relative glass rounded-2xl p-6 h-full flex flex-col hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <service.Logo className="h-8 w-8 text-white" />
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">{service.name}</h3>
                  <span className="text-[10px] font-medium bg-primary/20 text-primary px-2 py-0.5 rounded-full uppercase tracking-wider">
                    {service.status}
                  </span>
                </div>
                
                <p className="text-muted-foreground text-sm flex-1">
                  {service.description}
                </p>
                
                <div className="mt-4 pt-4 border-t border-border/50">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-between group-hover:text-primary group-hover:bg-primary/10 transition-all"
                    asChild
                  >
                    <a href="#contact">
                      LEARN MORE
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Website Design Add-on */}
        <motion.div
          initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reduceAnimations ? 0 : 0.4, delay: reduceAnimations ? 0 : 0.2 }}
          className="mt-10 glass rounded-2xl p-8 text-center hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Globe className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold">Need a Website?</h3>
          </div>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            Don't have a landing page to send traffic to? We also offer high-converting 
            website design as an add-on service to maximize your AI ad campaigns.
          </p>
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all"
            asChild
          >
            <a href="#contact">ASK ABOUT WEBSITE DESIGN</a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
